import { useEffect, useRef, useState, useTransition } from 'react';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import AOS from 'aos';
import 'aos/dist/aos.css';
import { Form, Button } from 'react-bootstrap';
import config from '../config';

import DocumentMeta from 'react-document-meta';

const meta = {
  title: 'Contact | Free medication abortion',
  description: '',
  meta: {
    charset: 'utf-8',
    name: {
      keywords: '',
    },
    property: {
      'og:title': 'Contact | Free medication abortion',
      'og:description': '',
      'og:image': '',
    },
  },
};

export default function Contact() {
  const initialFormState = {
    fullname: '',
    email: '',
    last_period_date: '',
    confirmation_test_date: '',
    address: '',
    apartment: '',
    city: '',
    zipcode: '',
    region: '',
    region_contact: '',
    other_info: '',
  };

  const [formData, setFormData] = useState(initialFormState);
  const [genericMessage, setGenericMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const [regions, setRegions] = useState('');
  const [searchParams] = useSearchParams();
  const mailToref = useRef('');
  const navigate = useNavigate();
  const messageRef = useRef(null);
  const [_, startTransition] = useTransition();

  const location = useLocation();

  useEffect(() => {
    AOS.init({ duration: 1000 });

    if (location.hash === '#contact-form') {
      const element = document.getElementById('contact-form');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }, [location]);

  useEffect(() => {
    if (location.state?.message) {
      setGenericMessage(location.state?.message);
      setMessageType(location.state?.messageType || 'success');
    }
  }, [navigate, location]);

  useEffect(() => {
    const fetchRegions = async () => {
      try {
        const response = await fetch(
          `${config.contentBaseUrl}/content/items/region?fields={"name":1, "email":1}&sort={"name":1}`,
        );
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();
        setRegions(result);
      } catch (error) {
        console.log(error.message);
      } finally {
        // console.log(regions);
      }
    };

    fetchRegions();
  }, []);

  useEffect(() => {
    const region_id = searchParams.get('region_id');
    if (region_id && regions.length) {
      setFormData({ ...formData, region: region_id });
    }
  }, [searchParams, regions]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const regionData = regions.filter(
        (region) => region._id == formData.region,
      )[0];

      formData.region = regionData.name;
      formData.region_contact = regionData.email;

      const res = await fetch(`${config.apiBaseUrl}/kit-request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      const response = await res.json();

      setGenericMessage(response.message || 'Form submitted successfully!');
      setMessageType(res.status == 200 ? 'success' : 'error');

      if (res.status === 200) {
        const region_id = searchParams.get('region_id');
        setFormData({ ...initialFormState, region: region_id });
      } else {
        const region_id = searchParams.get('region_id');
        if (region_id && regions.length) {
          setFormData({ ...formData, region: region_id });
        }
      }
    } catch (err) {
      const region_id = searchParams.get('region_id');
      if (region_id && regions.length) {
        setFormData({ ...formData, region: region_id });
      }
    }

    startTransition(() => {
      setTimeout(() => {
        messageRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }, 100);
    });
  };

  return (
    <DocumentMeta {...meta}>
      <div className="contact-page">
        <div className="page-title dark-background text-start contact-bg">
          <div ref={messageRef} className="container position-relative">
            <h1>Contact</h1>
          </div>
        </div>

        <section className=" contact-form section light-background py-3">
          <div className="container py-5">
            {genericMessage && (
              <div
                className={`mt-3 alert alert-${messageType === 'success' ? 'success' : 'danger'}`}
              >
                {genericMessage}
              </div>
            )}

            <div className="row shadow rounded p-4">
              <div className="col-md-6 border-end mb-4 mb-md-0">
                <h4 className="mb-4">Need Help?</h4>
                <div>
                  The request must be made by the pregnant person themselves.
                  Please make sure all information is accurate before
                  submitting.
                  <br />
                  <ul>
                    <li>Name of the person receiving the medication</li>
                    <li>Full mailing address</li>
                    <li>Start date of last full menstrual period</li>
                    <li>Please only submit one request</li>
                    <li>Confirmation will be sent to you within 24-48 hours</li>
                  </ul>
                  <a
                    href="mailto:"
                    style={{ display: 'none' }}
                    ref={mailToref}
                  />
                </div>
              </div>

              <div className="col-md-6 px-4" id="contact-form">
                <h4 className="mb-4">Request form</h4>
                <Form onSubmit={handleSubmit}>
                  <Form.Group className="mb-3" controlId="formName">
                    <Form.Label>Full Name</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter your name"
                      required
                      value={formData.fullname}
                      onChange={(e) =>
                        setFormData({ ...formData, fullname: e.target.value })
                      }
                    />
                  </Form.Group>

                  <Form.Group className="mb-3" controlId="formEmail">
                    <Form.Label>Email Address</Form.Label>
                    <Form.Control
                      type="email"
                      placeholder="Enter your email"
                      required
                      value={formData.email}
                      onChange={(e) =>
                        setFormData({ ...formData, email: e.target.value })
                      }
                    />
                  </Form.Group>

                  <Form.Group className="mb-3" controlId="formLastPeriodDate">
                    <Form.Label>
                      Start Date of Last Full Period - Date Must Be Accurate
                    </Form.Label>
                    <Form.Control
                      type="date"
                      placeholder="Date of last period"
                      required
                      value={formData.last_period_date}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          last_period_date: e.target.value,
                        })
                      }
                    />
                  </Form.Group>

                  <Form.Group
                    className="mb-3"
                    controlId="formConfirmationTestDate"
                  >
                    <Form.Label>
                      Date of Positive Pregnancy Test - Date Must Be Accurate
                    </Form.Label>
                    <Form.Control
                      type="date"
                      placeholder="Date of Confirmation Test"
                      required
                      value={formData.confirmation_test_date}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          confirmation_test_date: e.target.value,
                        })
                      }
                    />
                  </Form.Group>

                  <Form.Group className="mb-3" controlId="formAddress">
                    <Form.Label>Street address</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter your street address"
                      required
                      value={formData.address}
                      onChange={(e) => {
                        setFormData({ ...formData, address: e.target.value });
                      }}
                    />
                  </Form.Group>

                  <Form.Group className="mb-3" controlId="formAddress">
                    <Form.Label>
                      Apartment Number - If None, Write NA
                    </Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter your apartment/suite number"
                      required
                      value={formData.apartment}
                      onChange={(e) => {
                        setFormData({ ...formData, apartment: e.target.value });
                      }}
                    />
                  </Form.Group>

                  <Form.Group className="mb-3" controlId="formCity">
                    <Form.Label>City</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter your city"
                      required
                      value={formData.city}
                      onChange={(e) => {
                        setFormData({ ...formData, city: e.target.value });
                      }}
                    />
                  </Form.Group>

                  <Form.Group className="mb-3" controlId="formZipcode">
                    <Form.Label>Zip Code</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter your zip code"
                      required
                      value={formData.zipcode}
                      onChange={(e) => {
                        setFormData({ ...formData, zipcode: e.target.value });
                      }}
                    />
                  </Form.Group>

                  {regions && (
                    <Form.Group className="mb-3" controlId="formRegionId">
                      <Form.Label>State</Form.Label>
                      <Form.Select
                        required
                        value={formData.region}
                        onChange={(e) => {
                          setFormData({
                            ...formData,
                            region: e.target.value,
                          });
                        }}
                      >
                        <option>Select a state</option>
                        {regions.map(({ _id, name }) => (
                          <option key={_id} value={_id}>
                            {name}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  )}

                  <Form.Group className="mb-3" controlId="formOtherInfo">
                    <Form.Label>
                      Please share any health concerns, mailing issues, or other
                      issues we need to know about.
                    </Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={4}
                      value={formData.other_info}
                      onChange={(e) => {
                        setFormData({
                          ...formData,
                          other_info: e.target.value,
                        });
                      }}
                    />
                  </Form.Group>

                  <Button variant="primary" type="submit" className="w-100">
                    Submit Request
                  </Button>
                </Form>
              </div>
            </div>
          </div>
        </section>
      </div>
    </DocumentMeta>
  );
}
